/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  INDUSTRY_OPTIONS,
  SUPPORT_AREA_OPTIONS,
} from '../../../outreach/models/leads-criteria.model';

@Component({
  selector: 'app-technical-assistance-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './technical-assistance-form.component.html',
  styleUrl: './technical-assistance-form.component.scss',
})
export class TechnicalAssistanceFormComponent implements OnInit {
  @Input() initialValue: any;
  @Output() formSubmit = new EventEmitter<any>();

  technicalAssistanceForm: FormGroup;
  industryOptions = INDUSTRY_OPTIONS;
  supportAreas = SUPPORT_AREA_OPTIONS;
  submitAttempted = false;

  private supportAreasMap: Record<string, string> = {
    Marketing: 'marketing',
    'Finances/Accounting': 'finances_accounting',
    Strategy: 'strategy',
    'Legal Assistance/Questions': 'legal_assistance',
    'Business Succession Planning': 'business_succession',
    'Applying for a Grant': 'applying_for_grant',
    'Tax Structure/Tax support': 'tax_structure',
    'Site Selection': 'site_selection',
    'Human Resources': 'human_resources',
    'Operational Planning': 'operational_planning',
    'Applying for a loan or capital': 'applying_for_loan',
    'Banking Accounts/Systems': 'banking_accounts',
    Technology: 'technology',
    'IT Infrastructure': 'it_infrastructure',
    'Investment Readiness Support': 'investment_readiness',
    'Government Contracting': 'government_contracting',
    'Manufacturing Support': 'manufacturing_support',
  };

  constructor(private fb: FormBuilder) {
    this.technicalAssistanceForm = this.fb.group({
      industry: ['', Validators.required],
      supportAreas: this.fb.group(
        this.supportAreas.reduce((acc, area) => {
          acc[this.sanitizeKey(area)] = [false];
          return acc;
        }, {} as any),
      ),
      interestedInSellingBusiness: [''],
      interestedInEmployeeStrategies: [''],
      interestedInSuccessionPlanning: [''],
      workedWithSupportOrg: [''],
      willingToPay: [''],
    });

    this.setupConditionalValidation();
  }

  ngOnInit(): void {
    if (this.initialValue) {
      const patchData = { ...this.initialValue };

      // Convert selectedSupportAreas array back to supportAreas checkbox object
      if (Array.isArray(patchData.selectedSupportAreas)) {
        const supportAreasObj: Record<string, boolean> = {};
        this.supportAreas.forEach((area) => {
          const key = this.sanitizeKey(area);
          const backendValue = this.supportAreasMap[area];
          supportAreasObj[key] =
            patchData.selectedSupportAreas.includes(backendValue);
        });
        patchData.supportAreas = supportAreasObj;
      }

      this.technicalAssistanceForm.patchValue(patchData);
    }
  }

  private setupConditionalValidation() {
    const supportAreasGroup = this.technicalAssistanceForm.get(
      'supportAreas',
    ) as FormGroup;
    const businessSuccessionKey = this.sanitizeKey(
      'Business Succession Planning',
    );

    supportAreasGroup.valueChanges.subscribe(() => {
      const isBusinessSuccessionSelected = supportAreasGroup.get(
        businessSuccessionKey,
      )?.value;

      const sellingControl = this.technicalAssistanceForm.get(
        'interestedInSellingBusiness',
      );
      const employeeControl = this.technicalAssistanceForm.get(
        'interestedInEmployeeStrategies',
      );
      const successionControl = this.technicalAssistanceForm.get(
        'interestedInSuccessionPlanning',
      );

      if (isBusinessSuccessionSelected) {
        sellingControl?.setValidators([Validators.required]);
        employeeControl?.setValidators([Validators.required]);
        successionControl?.setValidators([Validators.required]);
      } else {
        sellingControl?.clearValidators();
        sellingControl?.setValue('');
        employeeControl?.clearValidators();
        employeeControl?.setValue('');
        successionControl?.clearValidators();
        successionControl?.setValue('');
      }

      sellingControl?.updateValueAndValidity();
      employeeControl?.updateValueAndValidity();
      successionControl?.updateValueAndValidity();
    });
  }

  isBusinessSuccessionPlanningSelected(): boolean {
    const supportAreasGroup = this.technicalAssistanceForm.get(
      'supportAreas',
    ) as FormGroup;
    const businessSuccessionKey = this.sanitizeKey(
      'Business Succession Planning',
    );
    return supportAreasGroup?.get(businessSuccessionKey)?.value || false;
  }

  sanitizeKey(area: string): string {
    return area.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  get hasErrors(): boolean {
    return this.submitAttempted && this.technicalAssistanceForm.invalid;
  }

  public onSubmitForm() {
    if (this.technicalAssistanceForm.valid) {
      const formValue = this.technicalAssistanceForm.value;
      const selectedSupportAreas = this.extractSelectedAreas(
        formValue.supportAreas,
      );
      this.formSubmit.emit({
        ...formValue,
        selectedSupportAreas,
      });
    } else {
      this.submitAttempted = true;
      this.markFormGroupTouched(this.technicalAssistanceForm);
    }
  }

  private extractSelectedAreas(
    supportAreas: Record<string, boolean>,
  ): string[] {
    if (!supportAreas) return [];

    return Object.entries(supportAreas)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        const displayLabel = this.supportAreas.find(
          (area) => this.sanitizeKey(area) === key,
        );
        return displayLabel ? this.supportAreasMap[displayLabel] : key;
      });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
